import React, { useState } from 'react';
import { useRecoilState } from 'recoil';
import MuiInput from '@material-ui/core/Input';
import SearchIcon from '@material-ui/icons/Search';
import Fade from '@material-ui/core/Fade';

import {
  FormGroup,
  FormLabel,
  FormError,
  ButtonFab,
} from '@Calix-Commerce/design-system/components';

import { searchState } from 'state/atoms';
import { defaultSearchState } from 'state/defaults';
import {
  PAST_WEEK_SEARCH_TYPE,
  PAST_MONTH_SEARCH_TYPE,
  CUSTOM_DATE_SEARCH_TYPE,
  OPEN_ORDERS_SEARCH_TYPE,
} from 'utils/constants';

import { getFormErrors } from './validation';
import { SEARCH_TYPE_OPTIONS, DEFAULT_FIELDS_BY_SEARCH_TYPE } from './defaults';

import { SearchWrapper, SearchTypeDropdown, SearchTermInput, DateInput } from './styledComponents';

const Search = ({ onSubmit, loading }) => {
  const [searchParams, setSearchParams] = useRecoilState(searchState);
  const [errors, setErrors] = useState({});

  const handleSearchTypeChange = (option) => {
    const searchType = option.id;

    const initialFields = DEFAULT_FIELDS_BY_SEARCH_TYPE[searchType] || defaultSearchState;
    setSearchParams({
      ...initialFields,
      searchType,
    });
    setErrors({}); // Clear all errors when switching search method
  };

  const handleFieldChange = (name) => (e) => {
    const newFields = {
      ...searchParams,
      [name]: e.target.value,
    };

    setErrors(getFormErrors(newFields) || {});
    setSearchParams(newFields);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = getFormErrors(searchParams);
    if (errors) {
      setErrors(errors);
    } else {
      onSubmit();
    }
  };

  /**
    Renders an uncontrolled date type field.
    Being uncontrolled prevents the value from being completely erased when the user enters
    an invalid date when editing the field manually.
  */
  const renderDateField = (name, label, defaultDate) => (
    <Fade in timeout={400}>
      <FormGroup error={errors[name]}>
        <FormLabel htmlFor={name}>{label}</FormLabel>
        <MuiInput
          id={name}
          type="date"
          disableUnderline
          defaultValue={defaultDate}
          onChange={handleFieldChange(name)}
          inputComponent={DateInput}
          inputProps={{ error: errors[name] }}
        />
        {errors[name] && <FormError>{errors[name]}</FormError>}
      </FormGroup>
    </Fade>
  );

  const renderSearchTermField = () => (
    <Fade in timeout={400}>
      <FormGroup error={errors.searchTerm} aria-describedby="search-term-text">
        <FormLabel htmlFor="search-term">Search Orders</FormLabel>
        <SearchTermInput
          id="search-term"
          autoComplete="off"
          autoFocus
          value={searchParams.searchTerm}
          error={errors.searchTerm}
          onChange={handleFieldChange('searchTerm')}
        />
        {errors.searchTerm && <FormError>{errors.searchTerm}</FormError>}
      </FormGroup>
    </Fade>
  );

  // Returns the inputs that correspond with the type of search being performed
  const renderFields = () => {
    if (searchParams.searchType === CUSTOM_DATE_SEARCH_TYPE) {
      const defaultDates = DEFAULT_FIELDS_BY_SEARCH_TYPE[CUSTOM_DATE_SEARCH_TYPE];

      return (
        <>
          {renderDateField('orderDateStart', 'Order Date (Start)', defaultDates.orderDateStart)}
          {renderDateField('orderDateEnd', 'Order Date (End)', defaultDates.orderDateEnd)}
        </>
      );
    } else if (
      searchParams.searchType !== PAST_WEEK_SEARCH_TYPE &&
      searchParams.searchType !== PAST_MONTH_SEARCH_TYPE &&
      searchParams.searchType !== OPEN_ORDERS_SEARCH_TYPE
    ) {
      return renderSearchTermField();
    }
  };

  return (
    <SearchWrapper onSubmit={handleSubmit}>
      {renderFields()}
      <FormGroup>
        <FormLabel>Search Method:</FormLabel>
        <SearchTypeDropdown
          size="lg"
          showRadios={false}
          selectedOptionId={searchParams.searchType}
          onSelectOption={handleSearchTypeChange}
          options={SEARCH_TYPE_OPTIONS}
        />
      </FormGroup>
      <ButtonFab
        onClick={handleSubmit}
        disabled={loading || Object.keys(errors).length > 0}
        style={{ marginTop: '1.32rem' }}
      >
        <SearchIcon />
      </ButtonFab>
    </SearchWrapper>
  );
};

export { Search };
